// RB Restoration Service Agreement — full digital, fillable, signable
// in-app version of their printed agreement. Auto-prefills from the job,
// saves to form_submissions, and provides print/download.
import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2, Save, Printer, ArrowLeft, FileSignature } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { isRBRestoration, RB_RESTORATION_ORG_ID } from "@/lib/orgConstants";
import { emptyAgreement, type RBAgreementData } from "@/components/service-agreement/agreementTypes";
import { AgreementPage1 } from "@/components/service-agreement/AgreementPage1";
import { AgreementPage2 } from "@/components/service-agreement/AgreementPage2";
import { AgreementPage3 } from "@/components/service-agreement/AgreementPage3";

const FORM_TEMPLATE_KEY = "rb-restoration-service-agreement";

export default function RBServiceAgreement() {
  const { jobId: jobIdParam } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { orgId, user } = useAuth();

  const jobId = jobIdParam || search.get("jobId") || "";
  const submissionId = search.get("submissionId") || "";

  const [data, setData] = useState<RBAgreementData>(emptyAgreement());
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const update = useCallback(
    <K extends keyof RBAgreementData>(k: K, v: RBAgreementData[K]) => {
      setData((prev) => ({ ...prev, [k]: v }));
    },
    []
  );

  // Load org logo + prefill from job (or load existing submission)
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: org } = await supabase
          .from("organizations")
          .select("logo_url")
          .eq("id", orgId)
          .single();
        if (!cancelled) setLogoUrl(org?.logo_url || null);

        if (submissionId) {
          const { data: sub } = await supabase
            .from("form_submissions")
            .select("submitted_data")
            .eq("id", submissionId)
            .single();
          if (sub?.submitted_data && !cancelled) {
            setData({ ...emptyAgreement(), ...(sub.submitted_data as Partial<RBAgreementData>) });
            setLoading(false);
            return;
          }
        }

        if (jobId) {
          const { data: job } = await supabase
            .from("jobs")
            .select("owner_name, owner_phone, owner_email, property_address, property_city, property_state, property_zip, claim_number")
            .eq("id", jobId)
            .single();
          if (job && !cancelled) {
            setData((prev) => ({
              ...prev,
              homeowner: job.owner_name || "",
              phone: job.owner_phone || "",
              email: job.owner_email || "",
              address: job.property_address || "",
              city: job.property_city || "",
              state_zip: [job.property_state, job.property_zip].filter(Boolean).join(" "),
              claim_number: job.claim_number || "",
              type_of_loss: "",
              cancel_customer_name: job.owner_name || "",
              cancel_phone: job.owner_phone || "",
              cancel_address: job.property_address || "",
              cert_address: job.property_address || "",
              cert_city: job.property_city || "",
              cert_state: job.property_state || "",
              cert_zip: job.property_zip || "",
              cert_claim_number: job.claim_number || "",
            }));
          }
        }
      } catch (e) {
        console.error("Failed to load agreement context", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, jobId, submissionId]);

  const ensureFormTemplate = useCallback(async (): Promise<string | null> => {
    if (!orgId) return null;
    const { data: existing } = await supabase
      .from("form_templates")
      .select("id")
      .eq("org_id", orgId)
      .eq("name", "RB Restoration Service Agreement")
      .maybeSingle();
    if (existing?.id) return existing.id;
    const { data: created, error } = await supabase
      .from("form_templates")
      .insert({
        org_id: orgId,
        name: "RB Restoration Service Agreement",
        description: "Digital retail service agreement (fillable + e-signature)",
        category: "customer",
        form_fields: [{ id: FORM_TEMPLATE_KEY, type: "json", label: "Agreement Data" }],
        is_system_template: false,
      })
      .select("id")
      .single();
    if (error) {
      console.error(error);
      return null;
    }
    return created?.id || null;
  }, [orgId]);

  const handleSave = async (status: "draft" | "submitted") => {
    if (!orgId || !jobId || !user) {
      toast.error("Job and login required");
      return;
    }
    if (status === "submitted" && !data.homeowner_signature) {
      toast.error("Homeowner signature required to send for e-signature");
      return;
    }
    setSaving(true);
    try {
      const templateId = await ensureFormTemplate();
      if (!templateId) throw new Error("Could not initialize agreement template");

      const payload = {
        form_template_id: templateId,
        job_id: jobId,
        org_id: orgId,
        submitted_data: data as any,
        status,
        submitted_by: user.id,
        submitted_at: status === "submitted" ? new Date().toISOString() : null,
        signature_data: {
          homeowner: data.homeowner_signature || null,
          representative: data.rep_signature || null,
          completion: data.cert_homeowner_signature || null,
        },
      };

      if (submissionId) {
        const { error } = await supabase
          .from("form_submissions")
          .update(payload)
          .eq("id", submissionId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("form_submissions").insert(payload);
        if (error) throw error;
      }

      toast.success(status === "draft" ? "Draft saved" : "Agreement saved & submitted");
      if (status === "submitted") navigate(`/jobs/${jobId}`);
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "Failed to save agreement");
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => window.print();

  const isAuthorized = useMemo(() => isRBRestoration(orgId), [orgId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-100">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-100 p-8">
        <div className="max-w-md text-center space-y-3">
          <FileSignature className="w-10 h-10 mx-auto text-muted-foreground" />
          <h1 className="text-xl font-semibold">Not available for your organization</h1>
          <p className="text-sm text-muted-foreground">
            This service agreement is configured for RB Restoration Builders only. (Org ID requirement:{" "}
            <code className="text-xs">{RB_RESTORATION_ORG_ID}</code>)
          </p>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" /> Go back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Toolbar (hidden on print) */}
      <div className="sticky top-0 z-20 bg-background border-b shadow-sm print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(jobId ? `/jobs/${jobId}` : "/forms")}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h2 className="font-semibold">RB Restoration — Service Agreement</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-1" /> Print / PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleSave("draft")} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save draft
            </Button>
            <Button size="sm" onClick={() => handleSave("submitted")} disabled={saving}>
              <FileSignature className="w-4 h-4 mr-1" /> Save & Submit
            </Button>
          </div>
        </div>
      </div>

      {/* Document */}
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8 print:space-y-0 print:p-0 print:max-w-none">
        <AgreementPage1 data={data} update={update} logoUrl={logoUrl} />
        <AgreementPage2 data={data} update={update} />
        <AgreementPage3 data={data} update={update} logoUrl={logoUrl} />
      </div>
    </div>
  );
}
